/**
 * Painel de Debug para Integração Spotify
 * Apenas para desenvolvimento - mostra status e permite testar funcionalidades
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useSpotifyAuth } from '@/hooks/useSpotifyAuth';
import { useSpotifyPlayback } from '@/hooks/useSpotifyPlayback';
import { musicMatchService } from '@/services/music-match-service';
import { Music, Play, Pause, SkipForward, SkipBack, RefreshCw, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export function SpotifyDebugPanel() {
  const auth = useSpotifyAuth();
  const playback = useSpotifyPlayback();
  const [testResult, setTestResult] = useState<string>('');
  const [configStatus, setConfigStatus] = useState<{clientId: string, redirectUri: string} | null>(null);

  useEffect(() => {
    checkConfig();
  }, []);

  const checkConfig = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-spotify-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const data = await response.json();
        setConfigStatus(data);
      }
    } catch (error) {
      console.error('Failed to check config:', error);
    }
  };

  const testGeneratePlaylist = async () => {
    try {
      setTestResult('Gerando playlist...');
      const result = await musicMatchService.generatePlaylist({
        bpm: 150,
        mood: 'energized',
        timeOfDay: 'morning',
        weather: 'sunny'
      });
      setTestResult(`✅ Playlist gerada: ${result.tracks.length} músicas`);
      toast.success('Playlist gerada com sucesso!');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setTestResult(`❌ Erro: ${errorMsg}`);
      toast.error('Erro ao gerar playlist');
    }
  };

  const testPlayTrack = async () => {
    try {
      if (!playback.isReady) {
        toast.error('Player não está pronto. Aguarde a inicialização ou verifique se tem Spotify Premium.');
        return;
      }
      
      setTestResult('Tocando música de teste...');
      // URI de exemplo do Spotify (The Weeknd - Blinding Lights)
      await playback.play('spotify:track:0VjIjW4GlUZAMYd2vXMi3b');
      setTestResult('✅ Música tocando!');
      toast.success('Reprodução iniciada!');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setTestResult(`❌ Erro: ${errorMsg}`);
      toast.error('Erro ao tocar música');
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Music className="h-5 w-5" />
          Spotify Debug Panel
        </CardTitle>
        <CardDescription>
          Painel de desenvolvimento para testar integração Spotify
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status de Configuração */}
        <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="font-medium">⚙️ Configuração</span>
            {configStatus ? (
              <Badge variant="default" className="gap-1">
                <Check className="h-3 w-3" />
                OK
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1">
                <X className="h-3 w-3" />
                Erro
              </Badge>
            )}
          </div>
          {configStatus && (
            <div className="text-xs space-y-1 text-muted-foreground">
              <p>Client ID: <code className="bg-background px-1 py-0.5 rounded">{configStatus.clientId.substring(0, 12)}...</code></p>
              <p>Redirect: <code className="bg-background px-1 py-0.5 rounded text-[10px]">{configStatus.redirectUri}</code></p>
            </div>
          )}
          {!configStatus && (
            <p className="text-xs text-destructive">
              ⚠️ Configuração não disponível. Verifique se SPOTIFY_CLIENT_ID está configurado.
            </p>
          )}
        </div>

        <Separator />

        {/* Status de Autenticação */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">🔐 Autenticação</span>
            {auth.isAuthenticated ? (
              <Badge variant="default" className="gap-1">
                <Check className="h-3 w-3" />
                Conectado
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <X className="h-3 w-3" />
                Desconectado
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            {auth.isAuthenticated ? (
              <Button onClick={auth.logout} variant="outline" size="sm">
                Desconectar
              </Button>
            ) : (
              <Button 
                onClick={() => auth.login()} 
                disabled={auth.isLoading || !configStatus}
                size="sm"
              >
                {auth.isLoading ? 'Conectando...' : 'Conectar Spotify'}
              </Button>
            )}
          </div>
          
          {!auth.isAuthenticated && !auth.isLoading && (
            <div className="text-xs p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
              <p className="font-medium text-amber-600 dark:text-amber-400 mb-1">📋 Checklist antes de conectar:</p>
              <ul className="space-y-0.5 text-muted-foreground">
                <li>✓ SPOTIFY_CLIENT_ID configurado no backend</li>
                <li>✓ Redirect URIs adicionados no Spotify Dashboard</li>
                <li>✓ Conta Spotify Premium ativa (necessário para player)</li>
              </ul>
            </div>
          )}
        </div>

        <Separator />

        {/* Status do Player */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">🎵 Player SDK</span>
            {playback.isReady ? (
              <Badge variant="default" className="gap-1">
                <Check className="h-3 w-3" />
                Pronto
              </Badge>
            ) : auth.isAuthenticated ? (
              <Badge variant="outline" className="gap-1 animate-pulse">
                <RefreshCw className="h-3 w-3" />
                Inicializando...
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <X className="h-3 w-3" />
                Não Inicializado
              </Badge>
            )}
          </div>
          
          {auth.isAuthenticated && !playback.isReady && (
            <div className="text-xs p-3 bg-blue-500/10 border border-blue-500/20 rounded-md text-muted-foreground">
              ⏳ Aguardando inicialização do SDK. Isso pode levar alguns segundos...
            </div>
          )}
          
          {playback.isReady && (
            <>
              <div className="text-sm text-muted-foreground">
                <p>Device ID: <code className="text-xs">{playback.deviceId}</code></p>
                <p>Status: {playback.isPlaying ? '▶️ Tocando' : '⏸️ Pausado'}</p>
                {playback.currentTrack && (
                  <p className="mt-2">
                    🎵 {playback.currentTrack.name} - {playback.currentTrack.artists?.[0]?.name}
                  </p>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={testPlayTrack}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Play className="h-4 w-4" />
                  Tocar Teste
                </Button>
                
                {playback.isPlaying ? (
                  <Button 
                    onClick={playback.pause}
                    size="sm"
                    variant="outline"
                  >
                    <Pause className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={playback.resume}
                    size="sm"
                    variant="outline"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                )}
                
                <Button 
                  onClick={playback.previous}
                  size="sm"
                  variant="outline"
                >
                  <SkipBack className="h-4 w-4" />
                </Button>
                
                <Button 
                  onClick={playback.next}
                  size="sm"
                  variant="outline"
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>

        <Separator />

        {/* Teste de Geração de Playlist */}
        <div className="space-y-3">
          <span className="font-medium">Geração de Playlist</span>
          <Button 
            onClick={testGeneratePlaylist}
            size="sm"
            variant="outline"
            className="gap-2"
            disabled={!auth.isAuthenticated}
          >
            <RefreshCw className="h-4 w-4" />
            Gerar Playlist Teste
          </Button>
          {testResult && (
            <p className="text-sm p-3 bg-muted rounded-md font-mono">
              {testResult}
            </p>
          )}
        </div>

        <Separator />

        {/* Troubleshooting */}
        <div className="space-y-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="font-medium text-sm">🔧 Problemas Comuns</p>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div>
              <p className="font-medium text-foreground">❌ "Conexão recusada" ou "redirect_uri_mismatch"</p>
              <p>→ Verifique se os 3 Redirect URIs estão no Spotify Dashboard:</p>
              <ul className="list-disc list-inside ml-3 mt-1 space-y-0.5">
                <li><code className="text-[10px] bg-background px-1 rounded">https://youbetterrun.app/oauth/spotify/callback</code></li>
                <li><code className="text-[10px] bg-background px-1 rounded">https://b7dcf91f-8602-412b-8094-e7ac4397cf3f.lovableproject.com/oauth/spotify/callback</code></li>
                <li><code className="text-[10px] bg-background px-1 rounded">http://localhost:5173/oauth/spotify/callback</code></li>
              </ul>
            </div>
            
            <div>
              <p className="font-medium text-foreground">⏱️ "Player não está pronto"</p>
              <p>→ Aguarde 5-10 segundos após conectar. Se persistir, reconecte.</p>
            </div>
            
            <div>
              <p className="font-medium text-foreground">🎵 "Spotify Premium necessário"</p>
              <p>→ O Web Playback SDK só funciona com contas Premium.</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Informações de Debug */}
        <div className="space-y-2 text-xs text-muted-foreground">
          <p className="font-medium">ℹ️ Informações:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Requer conta Spotify Premium</li>
            <li>Player inicializa automaticamente após login</li>
            <li>Tokens são atualizados automaticamente</li>
            <li>Playlists se adaptam ao BPM e mood</li>
          </ul>
        </div>

        {/* Links Úteis */}
        <div className="pt-4 border-t space-y-2">
          <p className="text-sm font-medium">📚 Documentação:</p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              asChild
            >
              <a href="https://github.com/yourusername/youbetterrun/blob/main/SPOTIFY_OAUTH_DIAGNOSTIC.md" target="_blank" rel="noopener noreferrer">
                📋 Guia Diagnóstico
              </a>
            </Button>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              asChild
            >
              <a href="https://github.com/yourusername/youbetterrun/blob/main/docs/SPOTIFY_QUICKSTART.md" target="_blank" rel="noopener noreferrer">
                ⚡ Quickstart
              </a>
            </Button>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              asChild
            >
              <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer">
                🎵 Spotify Dashboard
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
